import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Reply, Edit2, Trash2, MessageCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { discussionService } from '@/services/discussion.service';
import type { ForumDiscussionDto } from '@/types/discussion.types';
import {
  getDiscussionAuthorName,
  getDiscussionAuthorInitials,
  formatDiscussionDate,
  highlightMentions,
} from '@/types/discussion.types';
import ReactionButtons from './ReactionButtons';
import DiscussionForm from './DiscussionForm';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
interface DiscussionItemProps {
  discussion: ForumDiscussionDto;
  threadId: string;
  onUpdate: (updated: ForumDiscussionDto) => void;
  onDelete: (discussionId: string) => void;
  onReply: (discussion: ForumDiscussionDto) => void;
  depth?: number;
  maxDepth?: number;
}

export default function DiscussionItem({
  discussion,
  threadId,
  onUpdate,
  onDelete,
  onReply,
  depth = 0,
  maxDepth = 5,
}: DiscussionItemProps) {
  const { user } = useAuth();
  const [showReplies, setShowReplies] = useState(true);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState<'bottom' | 'top'>('bottom');
  const [menuAlign, setMenuAlign] = useState<'left' | 'right'>('right');
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [replies, setReplies] = useState<ForumDiscussionDto[]>(discussion.replies || []);
  const [hasMoreReplies, setHasMoreReplies] = useState(
    discussion.reply_count > (discussion.replies?.length || 0)
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const isAuthor = user?.id === discussion.user_id;
  const isDeleted = discussion.is_deleted;
  const authorName = getDiscussionAuthorName(discussion);
  const authorInitials = getDiscussionAuthorInitials(discussion);

  // Calculate menu position to avoid clipping at bottom and sides
  const calculateMenuPosition = () => {
    if (menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceRight = window.innerWidth - rect.right;
      const menuHeight = 120; // Approximate height of the menu
      const menuWidth = 192; // w-48 = 192px

      // If not enough space below, show menu above
      setMenuPosition(spaceBelow < menuHeight ? 'top' : 'bottom');

      // If not enough space on right, align to right edge of button
      // Otherwise align to left edge for better visual flow
      setMenuAlign(spaceRight < menuWidth ? 'right' : 'left');
    }
  };

  const toggleMenu = () => {
    if (!showMenu) {
      calculateMenuPosition();
    }
    setShowMenu(!showMenu);
  };
  const handleChildReply = (repliedToDiscussion: ForumDiscussionDto) => {
    setReplies(replies.map(r => 
      r._id === repliedToDiscussion._id 
        ? { ...r, reply_count: r.reply_count + 1 } 
        : r
    ));

    onReply(discussion);
  };

  // Load more replies with pagination
  const loadMoreReplies = async () => {
    try {
      setLoadingReplies(true);
      const response = await discussionService.getReplies(
        threadId,
        discussion._id,
        Math.ceil(replies.length / 5) + 1,
        5
      );
      setReplies([...replies, ...response.discussions]);
      setHasMoreReplies(response.discussions.length === 5);
    } catch (error) {
      toast.error('Failed to load more replies');
    } finally {
      setLoadingReplies(false);
    }
  };

  const handleReplySuccess = (newReply: ForumDiscussionDto) => {
    setReplies([newReply, ...replies]);
    setShowReplies(true); 
    setShowReplyForm(false);
    onReply(discussion); // Notify parent to update reply count
    toast.success('Reply posted!');
  };

 const handleEditSuccess = (updated: ForumDiscussionDto) => {
  setIsEditing(false);
  const updatedWithUserData = {
    ...discussion,
    ...updated,
    user: updated.user || discussion.user,
  };

  onUpdate(updatedWithUserData);
};
  const openDeleteModal = () => {
    setShowMenu(false);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setDeleteLoading(true);
      await discussionService.deleteDiscussion({
        thread_id: threadId,
        discussion_id: discussion._id,
      });
      onDelete(discussion._id);
      toast.success('Comment deleted successfully.');
      setDeleteModalOpen(false);
    } catch (error) {
      toast.error('Failed to delete comment.');
    } finally {
      setDeleteLoading(false);
    }
  };

 const handleReactionUpdate = (updated: ForumDiscussionDto) => {
  // Preserve existing user data if the update is missing it
  const updatedWithUserData = {
    ...discussion,
    ...updated,
    user: updated.user || discussion.user,
  };
  onUpdate(updatedWithUserData);
};

  const handleReplyUpdate = (updated: ForumDiscussionDto) => {
    setReplies(replies.map((r) => (r._id === updated._id ? updated : r)));
  };

  const handleReplyDelete = (discussionId: string) => {
    setReplies(replies.filter((r) => r._id !== discussionId));
    // Update parent reply count
    onUpdate({ ...discussion, reply_count: discussion.reply_count - 1 });
  };

  // Render deleted message
  if (isDeleted) {
    return (
      <div className={`py-3 ${depth > 0 ? 'ml-8 border-l-2 border-gray-200 pl-4' : ''}`}>
        <p className="text-sm text-gray-400 italic">[This comment has been deleted]</p>
      </div>
    );
  }

  return (
    <div className={`py-3 ${depth > 0 ? 'ml-8 border-l-2 border-gray-200 pl-4' : ''}`}>
      {/* Main Comment */}
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {discussion.user?.profile_picture ? (
            <img
              src={discussion.user.profile_picture}
              alt={authorName}
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-brand-indigo-100 text-brand-indigo-600 flex items-center justify-center text-xs font-semibold">
              {authorInitials}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-900">{authorName}</span>
            <span className="text-xs text-gray-500">{formatDiscussionDate(discussion.createdAt)}</span>
            {discussion.is_edited && (
              <span className="text-xs text-gray-400 italic">(edited)</span>
            )}
            {discussion.is_flagged && (
              <span className="px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded">Flagged</span>
            )}
          </div>

          {/* Message or Edit Form */}
          {isEditing ? (
            <div className="mb-2">
              <DiscussionForm
                threadId={threadId}
                existingDiscussion={discussion}
                onSuccess={handleEditSuccess}
                onCancel={() => setIsEditing(false)}
                placeholder="Edit your comment..."
                autoFocus
              />
            </div>
          ) : (
            <div
              className="text-sm text-gray-700 mb-2 break-words"
              dangerouslySetInnerHTML={{ __html: highlightMentions(discussion.message) }}
            />
          )}

          {/* Actions */}
          {!isEditing && (
            <div className="flex items-center gap-3 flex-wrap">
              {/* Reactions */}
              <ReactionButtons
                discussion={discussion}
                threadId={threadId}
                onReactionUpdate={handleReactionUpdate}
              />

              {/* Reply Button */}
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-brand-indigo-600 hover:bg-brand-indigo-50 rounded-full transition-all duration-150"
              >
                <Reply className="w-3.5 h-3.5" />
                <span>Reply</span>
              </button>

              {/* Menu (Edit/Delete) */}
              {isAuthor && (
                <div className="relative">
                  <button
                    ref={menuButtonRef}
                    onClick={toggleMenu}
                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all duration-150"
                    aria-label="More options"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {showMenu && (
                    <>
                      {/* Backdrop */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowMenu(false)}
                      />

                      {/* Dropdown Menu */}
                      <div className={`absolute ${
                        menuAlign === 'right' ? 'right-0' : 'left-0'
                      } ${
                        menuPosition === 'top' ? 'bottom-8' : 'top-8'
                      } w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-20 py-1.5 opacity-100 scale-100 transition-all duration-150 ${
                        menuPosition === 'top'
                          ? (menuAlign === 'right' ? 'origin-bottom-right' : 'origin-bottom-left')
                          : (menuAlign === 'right' ? 'origin-top-right' : 'origin-top-left')
                      }`}>
                        <button
                          onClick={() => {
                            setIsEditing(true);
                            setShowMenu(false);
                          }}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                          <span>Edit comment</span>
                        </button>
                        <div className="my-1 border-t border-gray-100" />
                        <button
                          onClick={openDeleteModal}
                          className="w-full px-4 py-2.5 text-left text-sm font-medium text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                          <span>Delete comment</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Reply Form */}
          {showReplyForm && !isEditing && (
            <div className="mt-3">
              <DiscussionForm
                threadId={threadId}
                parentId={discussion._id}
                replyToUser={{ id: discussion.user_id, name: authorName }}
                onSuccess={handleReplySuccess}
                onCancel={() => setShowReplyForm(false)}
                placeholder={`Reply to ${authorName}...`}
                autoFocus
              />
            </div>
          )}

          {/* Replies Section */}
          {(discussion.reply_count > 0 || replies.length > 0) && (
            <div className="mt-3">
              {/* Toggle Replies Button */}
              {replies.length === 0 ? (
                <button
                  onClick={() => {
                    setShowReplies(true);
                    if (replies.length === 0) loadMoreReplies();
                  }}
                  className="flex items-center gap-1.5 text-sm font-medium text-brand-indigo-600 hover:text-brand-indigo-700 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>
                    {Math.max(discussion.reply_count, replies.length) === 1
                      ? '1 reply'
                      : `${Math.max(discussion.reply_count, replies.length)} replies`}
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => setShowReplies(!showReplies)}
                  className="flex items-center gap-1.5 text-sm font-medium text-brand-indigo-600 hover:text-brand-indigo-700 transition-colors mb-2"
                >
                  {showReplies ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                  <span>
                    {showReplies ? 'Hide' : 'Show'}{' '}
                    {Math.max(discussion.reply_count, replies.length) === 1
                      ? '1 reply'
                      : `${Math.max(discussion.reply_count, replies.length)} replies`}
                  </span>
                </button>
              )}

              {/* Nested Replies */}
              {showReplies && replies.length > 0 && (
                <div className="space-y-0">
                  {replies.map((reply) => (
                    <DiscussionItem
                      key={reply._id}
                      discussion={reply}
                      threadId={threadId}
                      onUpdate={handleReplyUpdate}
                      onDelete={handleReplyDelete}
                       onReply={handleChildReply} 
                      depth={depth < maxDepth ? depth + 1 : depth}
                      maxDepth={maxDepth}
                    />
                  ))}

                  {/* Load More Replies */}
                  {hasMoreReplies && (
                    <button
                      onClick={loadMoreReplies}
                      disabled={loadingReplies}
                      className="ml-8 mt-2 text-sm font-medium text-brand-indigo-600 hover:text-brand-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingReplies ? 'Loading...' : 'Load more replies'}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Comment"
        description="Are you sure you want to delete this comment? This action cannot be undone."
        itemName="this comment"
        isLoading={deleteLoading}
      />
    </div>
  );
}
